/*
 * Day One Threads: product add-ons (Phase 3).
 *
 * Product page (print products, snippets/dot-addons.liquid):
 * - "Add lettuce hem" shows only for eligible styles (Bummies only With Skirt).
 * - "Add matching Dolly Gown" shows once a garment style is chosen, with its own quantity.
 * - When either is checked, Buy It Now is hidden and Add to Cart sends one /cart/add.js request:
 *     garment (+ property "Lettuce Hem: Yes" when hemmed)
 *     Lettuce Hem variant for that style, nested under the garment, same quantity as the garment
 *     Matching Dolly Gown variant of this print, as its own line
 *   With no add-on checked, Dawn's normal add to cart runs untouched.
 *
 * Cart (every page): after any cart change, each garment marked "Lettuce Hem: Yes" must have exactly one
 * nested hem line with the garment's quantity. Extra, orphaned or mismatched hem lines are corrected.
 * Shopify itself removes nested lines when their garment is removed.
 * While a hem line is being corrected, checkout shows 'Updating cart...' and waits (checkout guard below).
 * After a successful add, both add-on checkboxes reset to unchecked.
 */
(function () {
  if (window.DOTAddons) return;

  const HEM_HANDLE = 'lettuce-hem';
  const HEM_PROPERTY = 'Lettuce Hem';
  const GOWN_STYLE = 'Matching Dolly Gown';
  const MAX_GOWNS = 10;

  let hemMap = null;
  const getHemMap = () => {
    if (hemMap) return hemMap;
    try {
      hemMap = JSON.parse(document.getElementById('dot-hem-map')?.textContent || '{}');
    } catch (e) {
      hemMap = {};
    }
    return hemMap;
  };

  // Lettuce Hem variant title for a garment, or null when the garment is not eligible.
  function hemTitleFor(style, configuration) {
    if (!style || style === GOWN_STYLE) return null;
    if (style === 'Bummies') return configuration === 'With Skirt' ? 'Bummies, With Skirt' : null;
    return getHemMap()[style] ? style : null;
  }

  const jsonPost = (url, body) =>
    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then((response) => response.json());

  /* ---------- Product page ---------- */

  function currentPicker(sectionId) {
    const pickers = Array.from(
      document.querySelectorAll(`variant-selects[data-dependent-options][data-section="${sectionId}"]`)
    );
    return pickers.reverse().find((picker) => picker.isConnected && picker.style.display !== 'none') || null;
  }

  function selectionOf(picker) {
    if (!picker || picker.dataset.pickerState === 'empty') return { style: '', configuration: '' };
    const styleOption = picker.querySelector('select option[selected][data-option-value-id]');
    const style = styleOption?.value || picker.querySelector('select')?.value || '';
    const configuration =
      picker.querySelector('fieldset[data-option-position="3"] input[type="radio"]:checked')?.value || '';
    return { style, configuration };
  }

  function setAddonVisible(addon, visible) {
    if (!addon) return;
    addon.hidden = !visible;
    if (!visible) {
      const check = addon.querySelector('.dot-addon__check');
      if (check) check.checked = false;
      const qty = addon.querySelector('.dot-addon__qty');
      if (qty) qty.hidden = true;
    }
  }

  const isOn = (addon) => !!addon && !addon.hidden && addon.querySelector('.dot-addon__check')?.checked;

  function updateActive(box) {
    const productInfo = box.closest('product-info');
    const active = Array.from(box.querySelectorAll('.dot-addon')).some(isOn);
    productInfo?.classList.toggle('dot-addons-active', active);
  }

  function refresh(picker) {
    if (!picker) return;
    const box = document.getElementById(`dot-addons-${picker.dataset.section}`);
    if (!box) return;
    const { style, configuration } = selectionOf(picker);
    const hem = box.querySelector('[data-addon="hem"]');
    const gown = box.querySelector('[data-addon="gown"]');
    const hemTitle = hemTitleFor(style, configuration);

    if (hem) hem.dataset.hemTitle = hemTitle || '';
    setAddonVisible(hem, !!hemTitle);
    setAddonVisible(gown, !!style && style !== GOWN_STYLE);
    box.hidden = !Array.from(box.querySelectorAll('.dot-addon')).some((addon) => !addon.hidden);
    updateActive(box);
  }

  document.addEventListener('change', (event) => {
    const check = event.target.closest?.('.dot-addon__check');
    if (!check) return;
    const addon = check.closest('.dot-addon');
    const qty = addon.querySelector('.dot-addon__qty');
    if (qty) qty.hidden = !check.checked;
    updateActive(addon.closest('.dot-addons'));
  });

  function setLoading(productForm, loading) {
    const button = productForm.submitButton;
    const spinner = productForm.querySelector('.loading__spinner');
    if (loading) {
      button.setAttribute('aria-disabled', true);
      button.classList.add('loading');
      spinner?.classList.remove('hidden');
    } else {
      button.classList.remove('loading');
      button.removeAttribute('aria-disabled');
      spinner?.classList.add('hidden');
    }
  }

  // After a successful add, the add-ons go back to unchecked so the next garment does not repeat them.
  function resetAddons(box) {
    box.querySelectorAll('.dot-addon').forEach((addon) => {
      const check = addon.querySelector('.dot-addon__check');
      if (check) check.checked = false;
      const qty = addon.querySelector('.dot-addon__qty');
      if (qty) qty.hidden = true;
      const qtyInput = addon.querySelector('.dot-addon__qty-input');
      if (qtyInput) qtyInput.value = '1';
    });
    updateActive(box);
  }

  async function addWithAddons(productForm, form, box) {
    const button = productForm.submitButton;
    if (button.getAttribute('aria-disabled') === 'true' || button.hasAttribute('disabled')) return;
    productForm.handleErrorMessage();

    const formData = new FormData(form);
    const variantId = Number(formData.get('id'));
    const quantity = Math.max(1, parseInt(formData.get('quantity')) || 1);
    if (!variantId) return;

    const hem = box.querySelector('[data-addon="hem"]');
    const gown = box.querySelector('[data-addon="gown"]');
    const garment = { id: variantId, quantity };
    const items = [garment];

    if (isOn(hem)) {
      const hemId = getHemMap()[hem.dataset.hemTitle];
      if (!hemId) {
        productForm.handleErrorMessage('Lettuce hem is not available for this item.');
        return;
      }
      garment.properties = { [HEM_PROPERTY]: 'Yes' };
      items.push({ id: hemId, quantity, parent_id: variantId });
    }
    if (isOn(gown)) {
      const gownQty = Math.min(MAX_GOWNS, Math.max(1, parseInt(gown.querySelector('.dot-addon__qty-input')?.value) || 1));
      items.push({ id: Number(gown.dataset.variantId), quantity: gownQty });
    }

    const cart = productForm.cart;
    const body = { items };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
      cart.setActiveElement(document.activeElement);
    }

    setLoading(productForm, true);
    try {
      const response = await jsonPost(`${window.routes.cart_add_url}.js`, body);
      if (response.status) {
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'product-form',
          productVariantId: variantId,
          errors: response.errors || response.description,
          message: response.message,
        });
        productForm.handleErrorMessage(response.description || response.message);
        return;
      }
      if (!cart) {
        window.location = window.routes.cart_url;
        return;
      }

      // Dawn's cart notification and drawer focus on the added garment's line.
      const garmentLine = (response.items || []).find((item) => item.variant_id === variantId) || response.items?.[0];
      response.key = garmentLine?.key;
      response.id = garmentLine?.id;

      publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: response });
      cart.renderContents(response);
      if (cart.classList.contains('is-empty')) cart.classList.remove('is-empty');
      resetAddons(box);
    } catch (e) {
      console.error(e);
      productForm.handleErrorMessage(window.cartStrings?.error);
    } finally {
      setLoading(productForm, false);
    }
  }

  // Runs before Dawn's own submit handler; only takes over when an add-on is checked.
  document.addEventListener(
    'submit',
    (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement)) return;
      // getAttribute, because form.id returns the form's <input name="id"> on product forms.
      const formId = form.getAttribute('id');
      if (!formId) return;
      const box = document.querySelector(`.dot-addons[data-product-form="${formId}"]`);
      if (!box || !Array.from(box.querySelectorAll('.dot-addon')).some(isOn)) return;
      const productForm = form.closest('product-form');
      if (!productForm) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      addWithAddons(productForm, form, box);
    },
    true
  );

  /* ---------- Cart: keep hem lines matched to their garments ---------- */

  function planCartFixes(cart) {
    const updates = {};
    const adds = [];
    const byKey = Object.fromEntries(cart.items.map((item) => [item.key, item]));
    const hemsByParent = {};

    cart.items
      .filter((item) => item.handle === HEM_HANDLE)
      .forEach((hemLine) => {
        const parent = byKey[hemLine.parent_relationship?.parent_key];
        if (!parent || parent.properties?.[HEM_PROPERTY] !== 'Yes') {
          updates[hemLine.key] = 0; // orphaned or attached to an unhemmed garment
          return;
        }
        (hemsByParent[parent.key] ||= []).push(hemLine);
      });

    cart.items
      .filter((item) => item.handle !== HEM_HANDLE && item.properties?.[HEM_PROPERTY] === 'Yes')
      .forEach((garment) => {
        const hemLines = hemsByParent[garment.key] || [];
        if (!hemLines.length) {
          const options = Object.fromEntries((garment.options_with_values || []).map((o) => [o.name, o.value]));
          const hemId = getHemMap()[hemTitleFor(options.Style, options.Configuration)];
          if (hemId) adds.push({ id: hemId, quantity: garment.quantity, parent_line_key: garment.key });
          return;
        }
        hemLines.forEach((hemLine, index) => {
          const wanted = index === 0 ? garment.quantity : 0;
          if (hemLine.quantity !== wanted) updates[hemLine.key] = wanted;
        });
      });

    return { updates, adds };
  }

  async function refreshCartUI() {
    publish(PUB_SUB_EVENTS.cartUpdate, { source: 'dot-addons' });

    const footer = document.getElementById('main-cart-footer');
    const sectionIds = ['cart-icon-bubble'];
    if (footer?.dataset.id) sectionIds.push(footer.dataset.id);
    try {
      const sections = await (await fetch(`${window.routes.cart_url}?sections=${sectionIds.join(',')}`)).json();
      const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const source = parse(sections['cart-icon-bubble']).querySelector('.shopify-section');
        if (source) bubble.innerHTML = source.innerHTML;
      }
      if (footer && sections[footer.dataset.id]) {
        const target = footer.querySelector('.js-contents');
        const source = parse(sections[footer.dataset.id]).querySelector('.js-contents');
        if (target && source) target.innerHTML = source.innerHTML;
      }
    } catch (e) {
      console.error(e);
    }
  }

  /* ---------- Checkout guard while hem lines sync ----------
   * When a hemmed garment's quantity changes in the cart, checkout is held ("Updating cart...")
   * until Dawn's update and the hem correction have both finished. A checkout click made in that
   * window is remembered and continues as soon as the cart is in sync. If anything fails, or the
   * sync takes longer than SYNC_TIMEOUT, checkout is released without continuing.
   */
  const SYNC_CLASS = 'dot-cart-syncing';
  const SYNC_TIMEOUT = 6000;
  let syncing = false;
  let syncTimer = null;
  let lastActionAt = 0;
  let pendingCheckout = null;

  function syncStatus(text) {
    let status = document.getElementById('dot-cart-sync-status');
    if (!status) {
      status = document.createElement('p');
      status.id = 'dot-cart-sync-status';
      status.className = 'visually-hidden';
      status.setAttribute('role', 'status');
      status.setAttribute('aria-live', 'polite');
      document.body.appendChild(status);
    }
    status.textContent = text;
  }

  // fromCustomer: the customer just changed a hemmed garment, so any check already running is out of date.
  function startSync(fromCustomer = true) {
    if (fromCustomer) lastActionAt = performance.now();
    clearTimeout(syncTimer);
    syncTimer = setTimeout(() => endSync(false), SYNC_TIMEOUT);
    if (syncing) return;
    syncing = true;
    document.documentElement.classList.add(SYNC_CLASS);
    syncStatus('Updating cart…');
  }

  function endSync(succeeded) {
    if (!syncing) return;
    syncing = false;
    clearTimeout(syncTimer);
    document.documentElement.classList.remove(SYNC_CLASS);
    syncStatus('');
    const checkout = pendingCheckout;
    pendingCheckout = null;
    if (succeeded && checkout) continueToCheckout(checkout);
  }

  const dawnCartBusy = () => !!document.querySelector('.cart__items--disabled');

  function continueToCheckout(checkout) {
    const button =
      (checkout.id && document.getElementById(checkout.id)) ||
      (checkout.formId && document.querySelector(`#${checkout.formId} [name="checkout"]`)) ||
      document.querySelector('#checkout, [name="checkout"]');
    if (button) button.click();
    else window.location.href = `${window.Shopify?.routes?.root || '/'}checkout`;
  }

  // A quantity change on a garment marked "Lettuce Hem: Yes" means its hem line must follow.
  const isHemmedGarmentRow = (row) =>
    !!row && Array.from(row.querySelectorAll('dt')).some((dt) => dt.textContent.trim().startsWith(HEM_PROPERTY));

  document.addEventListener(
    'change',
    (event) => {
      const input = event.target;
      if (!input.matches?.('.quantity__input') || !input.closest('cart-items, cart-drawer-items')) return;
      if (isHemmedGarmentRow(input.closest('.cart-item'))) startSync();
    },
    true
  );

  const checkoutControl = (target) =>
    target.closest?.('[name="checkout"], #CartDrawer-Checkout, a[href$="/checkout"]');

  document.addEventListener(
    'click',
    (event) => {
      const control = checkoutControl(event.target);
      if (!control || !syncing) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      pendingCheckout = {
        id: control.id,
        formId: control.form?.getAttribute('id') || control.closest('form')?.getAttribute('id'),
      };
    },
    true
  );

  document.addEventListener(
    'submit',
    (event) => {
      if (!syncing || !event.submitter || !checkoutControl(event.submitter)) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      pendingCheckout = { id: event.submitter.id, formId: event.target.getAttribute('id') };
    },
    true
  );

  async function postOrThrow(url, body) {
    const result = await jsonPost(url, body);
    if (result?.status) throw new Error(result.description || result.message || 'Cart update failed');
    return result;
  }

  let running = false;
  let again = false;
  async function reconcileCart() {
    if (running) {
      again = true;
      return;
    }
    running = true;
    const startedAt = performance.now();
    let succeeded = true;
    try {
      for (let pass = 0; pass < 3; pass++) {
        const cart = await (await fetch(`${window.routes.cart_url}.js`)).json();
        const { updates, adds } = planCartFixes(cart);
        if (!Object.keys(updates).length && !adds.length) break;
        startSync(false);
        if (Object.keys(updates).length) await postOrThrow(`${window.routes.cart_update_url}.js`, { updates });
        if (adds.length) await postOrThrow(`${window.routes.cart_add_url}.js`, { items: adds });
        await refreshCartUI();
      }
    } catch (e) {
      succeeded = false;
      console.error(e);
    } finally {
      running = false;
      if (again) {
        again = false;
        reconcileCart();
      } else if (!succeeded) {
        endSync(false);
      } else if (lastActionAt <= startedAt && !dawnCartBusy()) {
        // Nothing changed in the cart since this check started, so it is in sync.
        endSync(true);
      }
    }
  }

  window.DOTAddons = { refresh, reconcileCart, planCartFixes };

  const init = () => {
    document
      .querySelectorAll('variant-selects[data-dependent-options]')
      .forEach((picker) => picker.style.display !== 'none' && refresh(picker));

    // PUB_SUB_EVENTS is a top-level const in Dawn's constants.js, so it is not on window.
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        if (event?.source === 'dot-addons') return;
        reconcileCart();
      });
    }
    if (document.querySelector('script[data-dot-addons]')?.dataset.cartCheck === 'true') reconcileCart();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
